import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mail, ArrowRight, ShieldCheck } from "lucide-react";
import { trackAdsConversion } from "@/lib/ads";

const SOCIAL_BTNS = [
  { id: "google", label: "Continue with Google", real: true },
];

export default function AuthPage() {
  const { login, register, otpRequest, otpVerify } = useAuth();
  const navigate = useNavigate();
  // Returning users default to Sign In; first-timers to Sign Up
  const isReturning = typeof window !== "undefined" && localStorage.getItem("vw_returning") === "1";
  const [tab, setTab] = useState(isReturning ? "signin" : "signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("candidate");
  const [loading, setLoading] = useState(false);

  // OTP flow
  const [otpId, setOtpId] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpName, setOtpName] = useState("");

  useEffect(() => { document.title = "Sign in · KARM"; }, []);

  const handleSocial = (provider) => {
    if (provider === "google") {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } else {
      toast.info(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login is mocked for MVP.`);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "signin") {
        await login(email, password);
      } else {
        await register({ email, password, name, role });
        trackAdsConversion("signup", { method: "email", role });
      }
      if (tab === "signup" && role === "recruiter") {
        toast.success("Account created. Recruiter access is reviewed by our team and unlocked within 24 hours.");
      } else {
        toast.success("Welcome to KARM.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    if (!otpId) return;
    if (!otpId.includes("@")) {
      toast.error("Please enter a valid email address. Mobile OTP is temporarily disabled.");
      return;
    }
    setLoading(true);
    try {
      const r = await otpRequest(otpId);
      setOtpSent(true);
      if (r.delivered_via === "resend") {
        toast.success(`OTP emailed to ${otpId}. Check your inbox.`);
      } else {
        toast.success(`If ${otpId} is valid, a code has been sent. Check your inbox.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      await otpVerify(otpId, otpCode, otpName);
      toast.success("Signed in.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-12 md:py-20 grid md:grid-cols-2 gap-12 items-start">
      <div className="hidden md:block md:sticky md:top-24">
        <p className="eyebrow mb-3">KARM</p>
        <h1 className="display-1 mb-5">A career platform built only for the elite GCC professional.</h1>
        <p className="text-muted-foreground text-lg max-w-md">
          Direct-source roles from JPMorgan, Goldman Sachs, Microsoft, Amazon, Deutsche Bank, Novartis & more — plus an AI Content Studio to grow your brand.
        </p>
        <div className="mt-8 flex items-center gap-3 text-sm text-muted-foreground">
          <ShieldCheck className="h-5 w-5 text-primary" />
          Encrypted sessions · No spam · Cancel anytime.
        </div>
      </div>

      <div className="max-w-md w-full mx-auto" data-testid="auth-card">
        <div className="vital-card-static p-6 md:p-8">
          <h2 className="text-2xl font-bold tracking-tight mb-1">Sign in</h2>
          <p className="text-sm text-muted-foreground mb-5">Choose how you&apos;d like to continue.</p>

          <div className="flex flex-col gap-2 mb-5">
            {SOCIAL_BTNS.map((b) => (
              <Button
                key={b.id}
                variant="outline"
                onClick={() => handleSocial(b.id)}
                data-testid={`social-${b.id}`}
                className="justify-start h-11"
              >
                <span className="font-semibold flex-1 text-left">{b.label}</span>
                {!b.real && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">mock</span>}
              </Button>
            ))}
          </div>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase tracking-widest">
              <span className="bg-card px-2 text-muted-foreground">or with email / OTP</span>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-3" data-testid="auth-tabs">
              <TabsTrigger value="signin" data-testid="tab-signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Sign Up</TabsTrigger>
              <TabsTrigger value="otp" data-testid="tab-otp">OTP</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleEmailAuth} className="space-y-3 mt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input id="signin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="signin-email" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signin-password">Password</Label>
                  <Input id="signin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="signin-password" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11" data-testid="signin-submit">
                  {loading ? "Signing in…" : (<>Sign in <ArrowRight className="ml-1 h-4 w-4" /></>)}
                </Button>
                <p className="text-xs text-muted-foreground text-center pt-2">
                  New to KARM?{" "}
                  <button type="button" onClick={() => setTab("signup")} className="text-primary font-semibold underline" data-testid="link-to-signup">
                    Create an account
                  </button>
                </p>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleEmailAuth} className="space-y-3 mt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="signup-name">Name</Label>
                  <Input id="signup-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" data-testid="signup-name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input id="signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="signup-email" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input id="signup-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="signup-password" />
                </div>
                <div className="space-y-1.5">
                  <Label>I am a…</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "candidate", label: "Seeker" },
                      { id: "recruiter", label: "Recruiter" },
                    ].map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setRole(r.id)}
                        data-testid={`role-${r.id}`}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                          role === r.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                  {role === "recruiter" && (
                    <p className="text-xs text-muted-foreground" data-testid="recruiter-role-note">
                      Recruiter access is verified by our team and unlocked within 24 hours.
                    </p>
                  )}
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11" data-testid="signup-submit">
                  {loading ? "Creating…" : "Create account"}
                </Button>
                <p className="text-xs text-muted-foreground text-center pt-2">
                  Already have an account?{" "}
                  <button type="button" onClick={() => setTab("signin")} className="text-primary font-semibold underline" data-testid="link-to-signin">
                    Sign in
                  </button>
                </p>
              </form>
            </TabsContent>

            <TabsContent value="otp">
              <div className="space-y-3 mt-4">
                {!otpSent ? (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="otp-id">Email</Label>
                      <Input
                        id="otp-id"
                        type="email"
                        placeholder="you@email.com"
                        value={otpId}
                        onChange={(e) => setOtpId(e.target.value)}
                        data-testid="otp-identifier"
                      />
                      <p className="text-xs text-muted-foreground">We&apos;ll email you a 6-digit code. Mobile OTP is temporarily disabled.</p>
                    </div>
                    <Button onClick={sendOtp} disabled={!otpId || loading} className="w-full h-11" data-testid="otp-send">
                      <Mail className="mr-2 h-4 w-4" /> Email me the code
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="otp-code">6-digit code</Label>
                      <Input id="otp-code" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value)} className="font-mono tracking-widest text-center text-lg" data-testid="otp-code" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="otp-name">Display name (new users)</Label>
                      <Input id="otp-name" value={otpName} onChange={(e) => setOtpName(e.target.value)} placeholder="Optional — used if creating a new account" data-testid="otp-name" />
                    </div>
                    <Button onClick={verifyOtp} disabled={!otpCode || loading} className="w-full h-11" data-testid="otp-verify">
                      Verify & continue
                    </Button>
                    <button onClick={() => { setOtpSent(false); setOtpCode(""); }} className="text-xs text-muted-foreground underline" data-testid="otp-restart">
                      Use a different identifier
                    </button>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            By continuing you agree to our terms. <Link to="/" className="underline">Back to home</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
